import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import Periodisation from "@/components/periodisation/Periodisation";
import { ChevronLeft } from "lucide-react";

/**
 * Programmes → Periodisation. Main clinician location for the planner.
 * Opened from the sidebar it offers a patient picker; opened from a patient
 * case/programme shortcut (?patient=…&programme=…) it loads that context
 * directly. Reuses the existing planner — one plan per programme, no new one.
 */
export default function ClinicianPeriodisation() {
  const [sp, setSp] = useSearchParams();
  const patientId = sp.get("patient");
  const programmeQ = sp.get("programme");
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [ctx, setCtx] = useState(null);     // { programme }
  const [loading, setLoading] = useState(false);
  const [noProg, setNoProg] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [patientName, setPatientName] = useState(null);

  useEffect(() => {
    if (patientId) return;
    setListLoading(true);
    api.get("/caseload")
      .then((r) => setPatients(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {})
      .finally(() => setListLoading(false));
  }, [patientId]);

  useEffect(() => {
    if (!patientId) { setCtx(null); setNoProg(false); setPatientName(null); return; }
    setLoading(true); setNoProg(false);
    Promise.all([
      api.get(`/programmes/patient/${patientId}`),
      api.get(`/patients/${patientId}`).catch(() => null),
    ])
      .then(([pr, pt]) => {
        setPatientName(pt?.data?.name || pt?.data?.email || null);
        if (!pr.data) { setNoProg(true); setCtx(null); } else setCtx({ programme: pr.data });
      })
      .catch(() => { toast.error("Could not load this patient's programme."); setNoProg(true); })
      .finally(() => setLoading(false));
  }, [patientId]);

  const term = q.trim().toLowerCase();
  const hits = term
    ? patients.filter((p) => `${p.name || ""} ${p.email || ""}`.toLowerCase().includes(term)).slice(0, 12)
    : patients.slice(0, 12);

  return (
    <DashLayout>
      <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="clinician-periodisation">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="fl-label text-xs text-zinc-400">Programmes</div>
            <h1 className="fl-heading text-2xl">Periodisation</h1>
          </div>
          {patientId && (
            <button onClick={() => setSp({})} className="fl-btn-ghost text-xs" data-testid="peri-change-patient">
              <ChevronLeft size={13} /> Change patient
            </button>
          )}
        </div>

        {!patientId && (
          <div className="fl-card p-4" data-testid="peri-patient-selector">
            <div className="fl-label mb-2">Choose a patient and programme to plan</div>
            <input className="fl-input w-full text-sm" placeholder="Search your caseload by name or email…"
                   value={q} onChange={(e) => setQ(e.target.value)} data-testid="peri-patient-search" />
            <div className="mt-2 border border-white/10 rounded-lg divide-y divide-white/5 max-h-80 overflow-auto">
              {listLoading && patients.length === 0 && <div className="px-3 py-3 text-sm text-zinc-500">Loading your patients…</div>}
              {!listLoading && hits.length === 0 && <div className="px-3 py-3 text-sm text-zinc-500">No patients found.</div>}
              {hits.map((p) => (
                <button key={p.id} onClick={() => setSp({ patient: p.id })}
                        className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center justify-between"
                        data-testid={`peri-patient-opt-${p.id}`}>
                  <span className="flex flex-col min-w-0">
                    <span className="text-sm text-white truncate">{p.name || p.email}</span>
                    {p.email && p.name && <span className="text-[11px] text-zinc-500 truncate">{p.email}</span>}
                  </span>
                  <span className="text-[11px] text-[#f47a20] shrink-0 ml-2">Plan →</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {patientId && loading && <div className="fl-card p-8 text-center text-sm text-zinc-400" data-testid="peri-loading">Loading…</div>}
        {patientId && !loading && noProg && (
          <div className="fl-card p-8 text-center" data-testid="peri-no-programme">
            <div className="fl-heading text-lg">No active programme</div>
            <p className="text-sm text-zinc-400 mt-2">This patient has no active programme yet. Create one in the patient's <b>Cases &amp; Programmes</b> tab, then return here.</p>
          </div>
        )}
        {patientId && !loading && ctx?.programme && (
          <Periodisation
            patientId={patientId}
            patientName={patientName}
            programmeId={programmeQ || ctx.programme.id}
            programme={ctx.programme}
          />
        )}
      </div>
    </DashLayout>
  );
}
